import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from 'recharts';
import { Trophy, Star, Package, Clock, CalendarDays, Activity, ShieldCheck } from 'lucide-react';
import Avatar from '@/components/Avatar';
const BRAND = '#0B4F3F';
const ACCENT = '#0B4F3F';

function MetricChip({ icon: Icon, label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-3 bg-[#F6F8FB] border border-[#E4E8EF] rounded-xl px-4 py-3"
    >
      <div className="w-9 h-9 rounded-lg bg-[#EFF2F7] flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#0B4F3F]" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-[#5B6675]">{label}</p>
        <p className="text-base font-bold text-[#0B1620] font-['Bricolage_Grotesque'] leading-tight">{value}</p>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-[#F6F8FB] border border-[#E4E8EF] rounded-2xl p-4"
    >
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque']">{title}</h4>
        {subtitle && <p className="text-[11px] text-[#5B6675] mt-0.5">{subtitle}</p>}
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const { api } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [lbRes, upRes] = await Promise.all([
        api.get('/admin/leaderboard', { params: { limit: 10 } }),
        api.get('/admin/usage-patterns', { params: { days: 30 } }),
      ]);
      setLeaderboard(lbRes.data.leaderboard || []);
      setUsage(upRes.data);
    } catch {} finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const formatHour = (h) => {
    const d = new Date();
    d.setHours(h, 0, 0, 0);
    return d.toLocaleTimeString([], { hour: 'numeric', hour12: true }).replace(' ', '');
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1,2,3].map(i => (
          <div key={i} className="h-32 rounded-2xl bg-[#F6F8FB] border border-[#E4E8EF] animate-pulse" />
        ))}
      </div>
    );
  }

  const maxHourBucket = Math.max(...(usage?.by_hour || []).map(b => b.count), 1);

  return (
    <div className="space-y-5" data-testid="admin-analytics">
      {/* Highlights */}
      <div className="grid grid-cols-2 gap-3">
        <MetricChip
          icon={Clock}
          label="Busiest hour"
          value={usage?.busiest_hour != null ? formatHour(usage.busiest_hour) : '—'}
          testId="busiest-hour-chip"
        />
        <MetricChip
          icon={CalendarDays}
          label="Busiest day"
          value={usage?.busiest_day || '—'}
          testId="busiest-day-chip"
        />
      </div>

      {/* Leaderboard */}
      <div className="bg-[#F6F8FB] border border-[#E4E8EF] rounded-2xl overflow-hidden" data-testid="leaderboard-card">
        <div className="px-4 py-3 border-b border-[#E4E8EF] flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#0B4F3F]" />
          <h3 className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque']">Top 10 Farmers</h3>
          <span className="ml-auto text-[10px] text-[#5B6675]">posts × (rating + 1)</span>
        </div>
        {leaderboard.length === 0 ? (
          <p className="text-sm text-[#5B6675] text-center py-8">No farmer activity yet.</p>
        ) : (
          <ul className="divide-y divide-[#E4E8EF]">
            {leaderboard.map((row, idx) => (
              <li
                key={row.farmer_id}
                data-testid={`leaderboard-row-${idx}`}
                className="px-4 py-3 flex items-center gap-3"
              >
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  idx === 0 ? 'bg-[#0B4F3F] text-[#F6F8FB]' :
                  idx === 1 ? 'bg-[#E4E8EF] text-[#0B1620]' :
                  idx === 2 ? 'bg-[#0B4F3F]/40 text-[#0B1620]' :
                  'bg-[#EFF2F7] text-[#5B6675]'
                }`}>{idx + 1}</span>

                <Avatar name={row.name} src={row.avatar} size={36} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-semibold text-[#0B1620] truncate">{row.name}</p>
                    {row.is_verified && <ShieldCheck className="w-3 h-3 text-[#15803D] flex-shrink-0" />}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-[#5B6675]">
                    <span className="flex items-center gap-1"><Package className="w-3 h-3" />{row.post_count} posts</span>
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3 fill-[#0B4F3F] text-[#0B4F3F]" />
                      {row.avg_rating || '0.0'}
                      <span className="text-[#5B6675]/70">({row.review_count})</span>
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-sm font-bold text-[#0B4F3F] font-['Bricolage_Grotesque']">{row.score}</p>
                  <p className="text-[10px] text-[#5B6675] uppercase tracking-wide">score</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Charts */}
      <ChartCard
        title="Activity by hour of day"
        subtitle={`Last ${usage?.period_days || 30} days · all events`}
        testId="hourly-chart"
      >
        <BarChart data={usage?.by_hour || []} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E4E8EF" vertical={false} />
          <XAxis
            dataKey="hour"
            tick={{ fill: '#5B6675', fontSize: 10 }}
            interval={2}
            tickFormatter={(h) => `${h}`}
          />
          <YAxis tick={{ fill: '#5B6675', fontSize: 10 }} allowDecimals={false} />
          <Tooltip
            cursor={{ fill: 'rgba(0,76,38,0.06)' }}
            contentStyle={{
              borderRadius: 12,
              border: '1px solid #E4E8EF',
              fontSize: 12,
            }}
            labelFormatter={(h) => `Hour ${h}:00`}
          />
          <Bar dataKey="count" radius={[6, 6, 0, 0]}>
            {(usage?.by_hour || []).map((b) => (
              <Cell
                key={b.hour}
                fill={b.count === maxHourBucket && maxHourBucket > 0 ? ACCENT : BRAND}
              />
            ))}
          </Bar>
        </BarChart>
      </ChartCard>

      <ChartCard
        title="Activity by day of week"
        subtitle={`Last ${usage?.period_days || 30} days · all events`}
        testId="weekday-chart"
      >
        <BarChart data={usage?.by_weekday || []} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E4E8EF" vertical={false} />
          <XAxis dataKey="day" tick={{ fill: '#5B6675', fontSize: 11 }} />
          <YAxis tick={{ fill: '#5B6675', fontSize: 10 }} allowDecimals={false} />
          <Tooltip
            cursor={{ fill: 'rgba(0,76,38,0.06)' }}
            contentStyle={{
              borderRadius: 12,
              border: '1px solid #E4E8EF',
              fontSize: 12,
            }}
          />
          <Bar dataKey="count" radius={[6, 6, 0, 0]}>
            {(usage?.by_weekday || []).map((b) => (
              <Cell
                key={b.day}
                fill={b.day === usage?.busiest_day ? ACCENT : BRAND}
              />
            ))}
          </Bar>
        </BarChart>
      </ChartCard>

      <p className="text-[11px] text-[#5B6675] text-center flex items-center justify-center gap-1">
        <Activity className="w-3 h-3" /> Patterns refresh every page load
      </p>
    </div>
  );
}
